import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video,
  Layers,
  AlertTriangle,
  CheckCircle2,
  Coins,
  Sparkles,
  Info,
  X,
} from 'lucide-react';
import { EduModal } from './EduModal.js';
import { EduButton } from './EduButton.js';
import { EduBadge } from './EduBadge.js';
import { api } from '../services/api.js';
import { Lesson, QuotaAccount } from '../types/index.js';

interface BatchRenderModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLessons: Lesson[];
  onSuccess: () => void;
}

export const BatchRenderModal: React.FC<BatchRenderModalProps> = ({
  isOpen,
  onClose,
  selectedLessons,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [quota, setQuota] = useState<QuotaAccount | null>(null);
  const [loadingQuota, setLoadingQuota] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Filter lessons that have at least 1 scene
  const validLessons = selectedLessons.filter((l) => (l.sceneCount || 0) > 0);
  const emptyLessons = selectedLessons.filter((l) => (l.sceneCount || 0) === 0);

  const creditsRequired = validLessons.length;
  const availableCredits = quota?.available ?? 0;
  const hasEnoughCredits = availableCredits >= creditsRequired;
  const remainingCredits = Math.max(0, availableCredits - creditsRequired);

  useEffect(() => {
    if (isOpen) {
      setErrorMessage(null);
      setLoadingQuota(true);
      api
        .getQuota()
        .then((q) => setQuota(q))
        .catch((err) => console.error('Erro ao carregar franquia:', err))
        .finally(() => setLoadingQuota(false));
    }
  }, [isOpen]);

  const handleConfirmBatchRender = async () => {
    if (validLessons.length === 0) {
      setErrorMessage('Nenhuma das aulas selecionadas possui cenas cadastradas para renderização.');
      return;
    }

    if (!hasEnoughCredits) {
      setErrorMessage(
        `Saldo insuficiente: você possui ${availableCredits} créditos disponíveis, mas selecionou ${creditsRequired} aulas.`
      );
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const lessonIds = validLessons.map((l) => l.id);
      const res = await api.renderBatchLessons(lessonIds);

      if (res.errors && res.errors.length > 0 && res.queuedCount === 0) {
        throw new Error(res.errors[0].error || 'Falha ao iniciar renderização das aulas.');
      }

      onSuccess();
      onClose();
      navigate('/processamentos');
    } catch (err: any) {
      setErrorMessage(err.message || 'Erro ao despachar renderização em lote.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EduModal
      isOpen={isOpen}
      onClose={() => !submitting && onClose()}
      title={
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-orange-50 text-brand-primary border border-orange-200">
            {selectedLessons.length === 1 && selectedLessons[0]?.status === 'completed' ? (
              <Video size={18} />
            ) : (
              <Video size={18} />
            )}
          </div>
          <span>
            {selectedLessons.length === 1
              ? selectedLessons[0]?.status === 'completed'
                ? 'Reprocessar Videoaula'
                : 'Renderizar Videoaula'
              : `Renderizar ${selectedLessons.length} Videoaulas em Lote`}
          </span>
        </div>
      }
      subtitle={
        selectedLessons.length === 1
          ? selectedLessons[0]?.status === 'completed'
            ? 'Confirme a nova renderização desta aula e o consumo de 1 crédito da franquia'
            : 'Confirme o envio da aula para a fila de renderização e o consumo de 1 crédito'
          : 'Confirme a fila de produção das aulas selecionadas e o consumo de créditos da franquia'
      }
      maxWidth="lg"
    >
      <div className="space-y-5">
        {/* Painel de Cálculo de Créditos */}
        <div className="bg-surface-secondary border border-bdr rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between border-b border-bdr/60 pb-2.5">
            <div className="flex items-center gap-2">
              <Coins size={16} className="text-brand-primary" />
              <span className="text-xs font-bold text-txt-primary uppercase tracking-wide">
                Consumo de Franquia / Créditos
              </span>
            </div>
            {loadingQuota ? (
              <span className="text-xs text-txt-muted animate-pulse">Carregando saldo...</span>
            ) : (
              <EduBadge variant={hasEnoughCredits ? 'success' : 'error'} size="sm" dot>
                {hasEnoughCredits ? 'Créditos Disponíveis' : 'Saldo Insuficiente'}
              </EduBadge>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-2.5 rounded-lg bg-surface border border-bdr">
              <p className="text-[11px] text-txt-secondary font-medium">Aulas Selecionadas</p>
              <p className="text-lg font-bold text-brand-primary">{creditsRequired}</p>
              <p className="text-[10px] text-txt-muted mt-0.5">
                {creditsRequired === 1 ? '1 crédito' : `${creditsRequired} créditos`}
              </p>
            </div>

            <div className="p-2.5 rounded-lg bg-surface border border-bdr">
              <p className="text-[11px] text-txt-secondary font-medium">Saldo Atual</p>
              <p className="text-lg font-bold text-txt-primary">
                {loadingQuota ? '—' : availableCredits}
              </p>
              <p className="text-[10px] text-txt-muted mt-0.5">créditos no ciclo</p>
            </div>

            <div className="p-2.5 rounded-lg bg-surface border border-bdr">
              <p className="text-[11px] text-txt-secondary font-medium">Saldo Após Envio</p>
              <p
                className={`text-lg font-bold ${
                  hasEnoughCredits ? 'text-status-success' : 'text-status-error'
                }`}
              >
                {loadingQuota ? '—' : remainingCredits}
              </p>
              <p className="text-[10px] text-txt-muted mt-0.5">créditos restantes</p>
            </div>
          </div>

          <div className="text-[11px] text-txt-secondary flex items-start gap-1.5 pt-1">
            <Info size={14} className="text-brand-primary shrink-0 mt-0.5" />
            <span>
              Cada videoaula renderizada com sucesso desconta 1 crédito da sua franquia mensal contratada.
              Em caso de falha de renderização, o crédito reservado é automaticamente estornado para o seu saldo.
            </span>
          </div>
        </div>

        {/* Lista de Aulas a Renderizar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold text-txt-primary flex items-center gap-1.5">
              <Layers size={14} className="text-brand-primary" />
              Aulas Selecionadas para Produção ({validLessons.length})
            </label>
            {emptyLessons.length > 0 && (
              <span className="text-[11px] text-status-error font-medium">
                {emptyLessons.length} {emptyLessons.length === 1 ? 'aula sem cenas (ignorada)' : 'aulas sem cenas (ignoradas)'}
              </span>
            )}
          </div>

          <div className="max-h-48 overflow-y-auto space-y-1.5 border border-bdr rounded-xl p-2 bg-surface">
            {validLessons.map((l) => (
              <div
                key={l.id}
                className="flex items-center justify-between p-2 rounded-lg bg-surface-secondary/50 border border-bdr/60 text-xs"
              >
                <div className="flex items-center gap-2 truncate pr-2">
                  <span className="font-mono font-bold text-txt-muted text-[11px]">
                    #{l.order}
                  </span>
                  <span className="font-semibold text-txt-primary truncate">
                    {l.title}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-txt-secondary bg-surface px-2 py-0.5 rounded border border-bdr">
                    {l.sceneCount} cenas
                  </span>
                  <EduBadge variant={l.status === 'completed' ? 'neutral' : 'info'} size="sm">
                    {l.status === 'completed' ? 'Reprocessar' : 'Novo'}
                  </EduBadge>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Alerta de Saldo Insuficiente */}
        {!hasEnoughCredits && !loadingQuota && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-status-error flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Créditos de franquia insuficientes</p>
              <p className="mt-0.5">
                Você selecionou {creditsRequired} aulas, mas possui apenas {availableCredits} créditos disponíveis no seu plano.
                Desmarque algumas aulas para prosseguir ou faça upgrade do seu plano.
              </p>
            </div>
          </div>
        )}

        {/* Mensagem de Erro Geral */}
        {errorMessage && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-status-error flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Rodapé e Ações */}
        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-bdr">
          <EduButton
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </EduButton>
          <EduButton
            type="button"
            variant="primary"
            size="sm"
            onClick={handleConfirmBatchRender}
            disabled={!hasEnoughCredits || validLessons.length === 0 || submitting || loadingQuota}
            isLoading={submitting}
            icon={<Video size={15} />}
          >
            {submitting
              ? 'Enviando para Fila...'
              : `Confirmar Renderização (${creditsRequired} ${creditsRequired === 1 ? 'aula' : 'aulas'})`}
          </EduButton>
        </div>
      </div>
    </EduModal>
  );
};
